import React from "react";
import { useTranslation } from "react-i18next";
import { fmtDateTime } from "../../lib/format";

// Stable backend action -> flat i18n key. The action itself contains a dot, so
// it can never be used directly as an i18next key.
//
// `platform.first_admin_bootstrapped` is mapped defensively only: that row is
// written with organization_id NULL, so it belongs to no organization and can
// never appear in an organization snapshot.
const ACTION_KEYS = {
  "platform.license_updated": "platform.audit_license_updated",
  "platform.first_admin_bootstrapped": "platform.audit_first_admin_bootstrapped",
  // Phase 2D.1C — provisioning, the Owner invitation and activation.
  "platform.organization_created": "platform.audit_organization_created",
  "platform.organization_updated": "platform.audit_organization_updated",
  "platform.organization_activated": "platform.audit_organization_activated",
  "platform.owner_invitation_created": "platform.audit_owner_invitation_created",
  "platform.owner_invitation_resent": "platform.audit_owner_invitation_resent",
  "platform.owner_invitation_replaced": "platform.audit_owner_invitation_replaced",
  "platform.owner_invitation_accepted": "platform.audit_owner_invitation_accepted",
  "platform.owner_invitation_delivery_failed":
    "platform.audit_owner_invitation_delivery_failed",
  // Phase 2D.1C checkpoint 3 — the destructive lifecycle.
  "platform.organization_archived": "platform.audit_organization_archived",
  "platform.organization_restored": "platform.audit_organization_restored",
  "platform.organization_deletion_requested":
    "platform.audit_organization_deletion_requested",
  "platform.organization_deletion_cancelled":
    "platform.audit_organization_deletion_cancelled",
  "platform.organization_purge_started": "platform.audit_organization_purge_started",
  "platform.organization_purged": "platform.audit_organization_purged",
  "platform.organization_purge_failed": "platform.audit_organization_purge_failed",
};

const SEAT_KEYS = [
  ["manager_seat_limit_before", "manager_seat_limit_after", "platform.manager_seats"],
  ["sales_seat_limit_before", "sales_seat_limit_after", "platform.sales_seats"],
];

/**
 * Platform audit history for one organization.
 *
 * The backend read model returns no actor, no name and no e-mail address, and
 * the audit metadata itself contains only statuses, seat integers and booleans —
 * never the internal note and never customer data. This component therefore has
 * nothing to redact.
 */
export const PlatformAuditHistory = ({ entries }) => {
  const { t } = useTranslation();
  const rows = Array.isArray(entries) ? entries : [];

  const changes = (metadata) => {
    const meta = metadata || {};
    const parts = [];
    if (meta.status_before !== meta.status_after) {
      parts.push(
        `${t("platform.status_label")}: ${t(`platform.status_${meta.status_before}`)} → ${t(
          `platform.status_${meta.status_after}`,
        )}`,
      );
    }
    SEAT_KEYS.forEach(([before, after, labelKey]) => {
      if (meta[before] !== meta[after]) {
        parts.push(`${t(labelKey)}: ${meta[before]} → ${meta[after]}`);
      }
    });
    if (meta.valid_until_changed) parts.push(t("platform.audit_validity_changed"));
    if (meta.name_changed) parts.push(t("platform.audit_name_changed"));
    if (meta.internal_note_changed) parts.push(t("platform.audit_note_changed"));
    if (meta.lifecycle_status_before && meta.lifecycle_status_after) {
      parts.push(
        t("platform.audit_lifecycle_changed", {
          before: t(`platform.lifecycle_${meta.lifecycle_status_before}`),
          after: t(`platform.lifecycle_${meta.lifecycle_status_after}`),
        }),
      );
    }
    if (meta.flow) parts.push(t(`platform.flow_${meta.flow}`));
    return parts;
  };

  return (
    <div className="qw-card" data-testid="platform-audit-history">
      <div style={{ fontWeight: 600, marginBottom: 12 }}>{t("platform.history")}</div>
      {rows.length === 0 ? (
        <div className="qw-empty" data-testid="platform-audit-empty">
          {t("platform.history_empty")}
        </div>
      ) : (
        <ul className="qw-platform-audit">
          {rows.map((entry) => (
            <li key={entry.id} data-testid={`platform-audit-row-${entry.id}`}>
              <div className="qw-platform-audit-head">
                <strong>
                  {ACTION_KEYS[entry.action]
                    ? t(ACTION_KEYS[entry.action])
                    : t("platform.audit_other")}
                </strong>
                <span className="qw-help">{fmtDateTime(entry.created_at)}</span>
              </div>
              {changes(entry.metadata).map((line) => (
                <div className="qw-help" key={line}>
                  {line}
                </div>
              ))}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PlatformAuditHistory;
